import { KnowledgeCategoryAccessLevel } from "shared/api/knowledgeItemCategories/api";

import { AssociatedVehiclesState } from "pages/shared/analytics/associatedVehicles/types";
import { LandingPageConfig } from "pages/shared/Dashboard/types";

import { FilterRowState } from "features/ui/Filters/FilterBuilder/types";

export const ENABLED_KEY = "enabled";

export const VEHICLES_PAGE_CONFIG_KEY = "vehicles";
export type VehiclesConfig = {
  eventTimeline?: boolean;
  // Ideally we don't manually enter eventTimelineFromDateVehicleAttribute but have a dropdown when configuring.
  // Default from date in event timeline
  // Priority 1: eventTimelineFromDateVehicleAttribute
  // Priority 2: eventTimelineFromDate
  eventTimelineFromDateVehicleAttribute?: string;
  eventTimelineFromDate?: string;
  eventTimelineToDate?: string;
  eventTimelineDefaultSignalEventFilters?: string;
  maintenanceSchedules?: boolean;
  tireDiagram?: boolean;
  ecus?: boolean;
  options?: boolean;
  parts?: boolean;
  vinViewPhotos?: boolean;
  vinViewAttachments?: boolean;
};

export const ASSETS_PAGE_CONFIG_KEY = "assets";
export type AssetsConfig = {
  eventTimeline?: boolean;
  eventTimelineFromDate?: string;
  eventTimelineToDate?: string;
  eventTimelineDefaultSignalEventFilters?: string;
  tireDiagram?: boolean;
  workOrders?: boolean;
  eventTimelineFromDateAssetAttribute?: string;
};

export const SERVICE_RECORDS_PAGE_CONFIG_KEY = "serviceRecords";
export type ServiceRecordsConfig = {
  hideMenuItem?: boolean;
  serviceRecordText?: string;
  staticFilters?: string;
};

export const FAILURE_MODES_PAGE_CONFIG_KEY = "failureModes";
export type FailureModesConfig = {
  similarVehicles?: boolean;
  survivalCurves?: boolean;
  nominalRiskModelPerformance?: boolean;
  riskDistribution?: boolean;
  showHideColumns?: boolean;
};

export const CLAIM_ANALYTICS_PAGE_CONFIG_KEY = "claimAnalytics";
export type ClaimAnalyticsConfig = {
  defaultFilters?: string;
  defaultVehicleFilters?: string;
  defaultQuickFilters?: FilterRowState[];
  defaultVehicleQuickFilters?: FilterRowState[];
  defaultAssociatedVehiclesState?: AssociatedVehiclesState;
};

export const SIGNAL_EVENTS_ANALYTICS_PAGE_CONFIG_KEY = "signalEventsAnalytics";
export type SignalEventsAnalyticsConfig = {
  defaultFilters?: string;
  defaultVehicleFilters?: string;
  defaultClaimGroupByAttribute?: string;
  defaultRepairGroupByAttribute?: string;
  defaultQuickFilters?: FilterRowState[];
  defaultVehicleQuickFilters?: FilterRowState[];
  defaultAssociatedVehiclesState?: AssociatedVehiclesState;
};

export const REPAIR_ANALYTICS_PAGE_CONFIG_KEY = "repairAnalytics";
export type RepairAnalyticsConfig = {
  defaultFilters?: string;
  defaultVehicleFilters?: string;
  defaultQuickFilters?: FilterRowState[];
  defaultVehicleQuickFilters?: FilterRowState[];
  defaultRepairGroupByAttribute?: string;
  defaultAssociatedVehiclesState?: AssociatedVehiclesState;
};

export const REPAIRS_PAGE_CONFIG_KEY = "repairs";

export const INSPECTION_ANALYTICS_PAGE_CONFIG_KEY = "inspectionAnalytics";
export type InspectionAnalyticsConfig = {
  defaultFilters?: string;
  defaultVehicleFilters?: string;
  defaultQuickFilters?: FilterRowState[];
  defaultVehicleQuickFilters?: FilterRowState[];
  defaultAssociatedVehiclesState?: AssociatedVehiclesState;
};

export const CUSTOM_SIGNAL_EVENTS_PAGE_CONFIG_KEY = "customSignalEvents";
export type CustomSignalEventsConfig = {
  eventPreviewFromDate?: string;
  eventPreviewToDate?: string;
};

export const HELP_ARTICLES_PAGE_CONFIG_KEY = "helpArticles";
type HelpArticle = {
  url: string;
  title: string;
};

export const LANDING_PAGE_PAGE_CONFIG_KEY = "landingPage";

export type HelpArticlesConfig = {
  articles: HelpArticle[];
};

export type TopHeaderConfig = {
  title: string;
  searchTextPlaceholder: string;
  tenantIconSvg?: string;
};

export type WorkOrdersConfig = {
  aisaPricing?: boolean;
  aisaServiceRecommendations?: boolean;
  workOrderParts?: boolean;
};

export type WorkOrderVerificationsConfig = {
  aisaPricing?: boolean;
  aisaServiceRecommendations?: boolean;
};

export const FLEETS_PAGE_CONFIG_KEY = "fleets";
export const DATA_EXPLORER_PAGE_CONFIG_KEY = "dataExplorer";
export const RELEASE_NOTES_PAGE_CONFIG_KEY = "releaseNotes";
export const ISSUES_PAGE_CONFIG_KEY = "issues";
export const VIADUCK_PAGE_CONFIG_KEY = "viaDuck";
export type ViaDuckConfig = {
  initialSuggestions?: string[];
  enabledCapabilities?: string[];
  speechToTextEnabled?: boolean;
  autoSubmitMode?: boolean;
};
export const AI_KNOWLEDGE_PAGE_CONFIG_KEY = "aiKnowledge";

// Page-wide visibility gate for AI Knowledge. Per-category permissions
// (visibility/editableBy/enabled) live on the knowledgeItemCategories table and
// are managed on the Knowledge Base Management page.
export type AIKnowledgeConfig = {
  visibility?: KnowledgeCategoryAccessLevel;
};

export const WORK_ORDERS_PAGE_CONFIG_KEY = "workOrders";
export const WORK_ORDERS_VERIFICATION_PAGE_CONFIG_KEY =
  "workOrdersVerification";
export const REPAIR_PARTS_PAGE_CONFIG_KEY = "repairParts";
export const REPAIR_LABOR_PAGE_CONFIG_KEY = "repairLabor";
export const TIRE_SETTINGS_PAGE_CONFIG_KEY = "tireSettings";
export const KPI_TARGETS_PAGE_CONFIG_KEY = "kpiTargets";
export const DUCK2SIEMS_JOB_STATUS_PAGE_CONFIG_KEY = "duck2SiemsJobStatus";
export const INGESTION_INFO_PAGE_CONFIG_KEY = "ingestionInfo";
export const DATA_TABLES_PAGE_CONFIG_KEY = "dataTables";
export const CONNECTIONS_PAGE_CONFIG_KEY = "connections";
export const MOBILE_GLOBAL_SEARCH_PAGE_CONFIG_KEY = "mobileGlobalSearch";
export const MOBILE_QR_SCANNER_PAGE_CONFIG_KEY = "mobileQrScanner";
export const MOBILE_OPERATOR_REQUESTS_PAGE_CONFIG_KEY =
  "mobileOperatorRequests";
export const DIRECTIVES_PAGE_CONFIG_KEY = "directives";
export const SCHEDULES_PAGE_CONFIG_KEY = "schedules";
export const GROUPS_PAGE_CONFIG_KEY = "groups";
export interface MobileQrScannerConfig {
  title?: string | null;
  infoTitle?: string | null;
  infoText?: string | null;
}
export interface MobileOperatorRequestsConfig {
  title?: string | null;
}
export const EXTERNAL_LINKS_PAGE_CONFIG_KEY = "externalLinks";
export interface ExternalLink {
  id: string;
  title: string;
  url: string;
  iconSvg?: string;
}
export interface ExternalLinksConfig {
  links?: ExternalLink[];
}
const TOP_HEADER_CONFIG_KEY = "topHeader";

export type KnownPageConfigKeys =
  | typeof VEHICLES_PAGE_CONFIG_KEY
  | typeof SERVICE_RECORDS_PAGE_CONFIG_KEY
  | typeof FAILURE_MODES_PAGE_CONFIG_KEY
  | typeof CLAIM_ANALYTICS_PAGE_CONFIG_KEY
  | typeof SIGNAL_EVENTS_ANALYTICS_PAGE_CONFIG_KEY
  | typeof CUSTOM_SIGNAL_EVENTS_PAGE_CONFIG_KEY
  | typeof FLEETS_PAGE_CONFIG_KEY
  | typeof REPAIR_ANALYTICS_PAGE_CONFIG_KEY
  | typeof REPAIRS_PAGE_CONFIG_KEY
  | typeof INSPECTION_ANALYTICS_PAGE_CONFIG_KEY
  | typeof HELP_ARTICLES_PAGE_CONFIG_KEY
  | typeof DATA_EXPLORER_PAGE_CONFIG_KEY
  | typeof RELEASE_NOTES_PAGE_CONFIG_KEY
  | typeof ISSUES_PAGE_CONFIG_KEY
  | typeof VIADUCK_PAGE_CONFIG_KEY
  | typeof WORK_ORDERS_PAGE_CONFIG_KEY
  | typeof WORK_ORDERS_VERIFICATION_PAGE_CONFIG_KEY
  | typeof REPAIR_PARTS_PAGE_CONFIG_KEY
  | typeof REPAIR_LABOR_PAGE_CONFIG_KEY
  | typeof ASSETS_PAGE_CONFIG_KEY
  | typeof AI_KNOWLEDGE_PAGE_CONFIG_KEY
  | typeof TIRE_SETTINGS_PAGE_CONFIG_KEY
  | typeof KPI_TARGETS_PAGE_CONFIG_KEY
  | typeof DUCK2SIEMS_JOB_STATUS_PAGE_CONFIG_KEY
  | typeof INGESTION_INFO_PAGE_CONFIG_KEY
  | typeof DATA_TABLES_PAGE_CONFIG_KEY
  | typeof CONNECTIONS_PAGE_CONFIG_KEY
  | typeof MOBILE_GLOBAL_SEARCH_PAGE_CONFIG_KEY
  | typeof MOBILE_QR_SCANNER_PAGE_CONFIG_KEY
  | typeof MOBILE_OPERATOR_REQUESTS_PAGE_CONFIG_KEY
  | typeof DIRECTIVES_PAGE_CONFIG_KEY
  | typeof SCHEDULES_PAGE_CONFIG_KEY
  | typeof GROUPS_PAGE_CONFIG_KEY
  | typeof EXTERNAL_LINKS_PAGE_CONFIG_KEY;

export type AllPageConfigKeys =
  KnownPageConfigKeys | typeof LANDING_PAGE_PAGE_CONFIG_KEY;

export const DEFAULT_CONFIG = {};

// Single source of truth for IDs that the UI still recognises. Any DB entry
// whose ID is not in this set is considered stale (e.g. a page that was
// removed without also removing its key here). Stale entries are stripped on
// read and on save so old values can't silently drive a future feature that
// reuses the same key.
export const VALID_PAGE_CONFIG_IDS: ReadonlySet<string> = new Set<string>([
  VEHICLES_PAGE_CONFIG_KEY,
  ASSETS_PAGE_CONFIG_KEY,
  SERVICE_RECORDS_PAGE_CONFIG_KEY,
  FAILURE_MODES_PAGE_CONFIG_KEY,
  CLAIM_ANALYTICS_PAGE_CONFIG_KEY,
  SIGNAL_EVENTS_ANALYTICS_PAGE_CONFIG_KEY,
  REPAIR_ANALYTICS_PAGE_CONFIG_KEY,
  REPAIRS_PAGE_CONFIG_KEY,
  INSPECTION_ANALYTICS_PAGE_CONFIG_KEY,
  CUSTOM_SIGNAL_EVENTS_PAGE_CONFIG_KEY,
  HELP_ARTICLES_PAGE_CONFIG_KEY,
  FLEETS_PAGE_CONFIG_KEY,
  DATA_EXPLORER_PAGE_CONFIG_KEY,
  RELEASE_NOTES_PAGE_CONFIG_KEY,
  ISSUES_PAGE_CONFIG_KEY,
  VIADUCK_PAGE_CONFIG_KEY,
  AI_KNOWLEDGE_PAGE_CONFIG_KEY,
  WORK_ORDERS_PAGE_CONFIG_KEY,
  WORK_ORDERS_VERIFICATION_PAGE_CONFIG_KEY,
  REPAIR_PARTS_PAGE_CONFIG_KEY,
  REPAIR_LABOR_PAGE_CONFIG_KEY,
  LANDING_PAGE_PAGE_CONFIG_KEY,
  TOP_HEADER_CONFIG_KEY,
  TIRE_SETTINGS_PAGE_CONFIG_KEY,
  KPI_TARGETS_PAGE_CONFIG_KEY,
  INGESTION_INFO_PAGE_CONFIG_KEY,
  DUCK2SIEMS_JOB_STATUS_PAGE_CONFIG_KEY,
  DATA_TABLES_PAGE_CONFIG_KEY,
  CONNECTIONS_PAGE_CONFIG_KEY,
  MOBILE_GLOBAL_SEARCH_PAGE_CONFIG_KEY,
  MOBILE_QR_SCANNER_PAGE_CONFIG_KEY,
  MOBILE_OPERATOR_REQUESTS_PAGE_CONFIG_KEY,
  DIRECTIVES_PAGE_CONFIG_KEY,
  SCHEDULES_PAGE_CONFIG_KEY,
  GROUPS_PAGE_CONFIG_KEY,
  EXTERNAL_LINKS_PAGE_CONFIG_KEY,
]);

// Here we can define page configs that have other settings besides just "enabled/disabled" flag
type PageConfigMap = {
  [VEHICLES_PAGE_CONFIG_KEY]: VehiclesConfig;
  [ASSETS_PAGE_CONFIG_KEY]: AssetsConfig;
  [SERVICE_RECORDS_PAGE_CONFIG_KEY]: ServiceRecordsConfig;
  [FAILURE_MODES_PAGE_CONFIG_KEY]: FailureModesConfig;
  [CLAIM_ANALYTICS_PAGE_CONFIG_KEY]: ClaimAnalyticsConfig;
  [SIGNAL_EVENTS_ANALYTICS_PAGE_CONFIG_KEY]: SignalEventsAnalyticsConfig;
  [REPAIR_ANALYTICS_PAGE_CONFIG_KEY]: RepairAnalyticsConfig;
  [INSPECTION_ANALYTICS_PAGE_CONFIG_KEY]: InspectionAnalyticsConfig;
  [CUSTOM_SIGNAL_EVENTS_PAGE_CONFIG_KEY]: CustomSignalEventsConfig;
  [HELP_ARTICLES_PAGE_CONFIG_KEY]: HelpArticlesConfig;
  [LANDING_PAGE_PAGE_CONFIG_KEY]: LandingPageConfig;
  [VIADUCK_PAGE_CONFIG_KEY]: ViaDuckConfig;
  [WORK_ORDERS_PAGE_CONFIG_KEY]: WorkOrdersConfig;
  [WORK_ORDERS_VERIFICATION_PAGE_CONFIG_KEY]: WorkOrderVerificationsConfig;
  [TOP_HEADER_CONFIG_KEY]: TopHeaderConfig;
  [AI_KNOWLEDGE_PAGE_CONFIG_KEY]: AIKnowledgeConfig;
  [MOBILE_QR_SCANNER_PAGE_CONFIG_KEY]: MobileQrScannerConfig;
  [MOBILE_OPERATOR_REQUESTS_PAGE_CONFIG_KEY]: MobileOperatorRequestsConfig;
  [EXTERNAL_LINKS_PAGE_CONFIG_KEY]: ExternalLinksConfig;
};

type PageConfigItem =
  | {
      [K in keyof PageConfigMap]: { ID: K; config: PageConfigMap[K] };
    }[keyof PageConfigMap]
  | { ID: Exclude<string, KnownPageConfigKeys>; config: typeof DEFAULT_CONFIG };

export type PagesConfig = PageConfigItem[];

// We reformat the config to be a map for easier access across the app
export type PagesConfigModel = {
  [K in keyof PageConfigMap]?: PageConfigMap[K];
} & Record<string, typeof DEFAULT_CONFIG | undefined>;

export type PageConfigValues =
  boolean | string | null | string[] | object | object[];

export interface PageConfigProps<T = typeof DEFAULT_CONFIG | undefined> {
  pageId: KnownPageConfigKeys;
  onConfigChange: (
    pageID: KnownPageConfigKeys,
    configKey: string,
    configValue: PageConfigValues
  ) => void;
  config: T;
}
